// frontend/src/components/HomeTab.jsx
import { useState } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import { SPORT_CATEGORIES, PERMANENT_SPORT_KEYS, CATEGORY_TO_CATS, getSportConfig } from '../config/sportsConfig';
import { getPrimaryLeagueKey, getAllowedLeagueCount, getCategoryOfActiveSport, splitLeagueName, groupLeaguesByPrefix, shouldGroupLeagues } from '../utils/homeTabHelpers';
import GameCard from './GameCard';
import { getUpcomingPreview } from '../utils/gamePreview';
import { colors, fontFamily } from '../theme';
import { useDragScroll } from '../hooks';

const categoryConfig = {
  'American Football': { icon: '🏈', abbrev: 'Football' },
  'Basketball':        { icon: '🏀', abbrev: 'Basketball' },
  'Baseball':          { icon: '⚾', abbrev: 'Baseball' },
  'Ice Hockey':        { icon: '🏒', abbrev: 'Hockey' },
  'Soccer':            { icon: '⚽', abbrev: 'Soccer' },
  'Combat Sports':     { icon: '🥊', abbrev: 'Combat' },
  'Golf':              { icon: '⛳', abbrev: 'Golf' },
  'Tennis':            { icon: '🎾', abbrev: 'Tennis' },
  'Aussie Rules':      { icon: '🏉', abbrev: 'AFL' },
  'Rugby':             { icon: '🏉', abbrev: 'Rugby' },
  'Cricket':           { icon: '🏏', abbrev: 'Cricket' },
  'Handball':          { icon: '🤾', abbrev: 'Handball' },
  'Volleyball':        { icon: '🏐', abbrev: 'Volleyball' },
  'Esports':           { icon: '🎮', abbrev: 'Esports' },
  'Table Tennis':      { icon: '🏓', abbrev: 'TT' },
  'Darts':             { icon: '🎯', abbrev: 'Darts' },
  'Snooker':           { icon: '🎱', abbrev: 'Snooker' },
};

const PREVIEW_COUNT = 3;

export default function HomeTab({
  enabledSports,
  onSelectCategory,
  activeTab,
  activeSport,
  onSelectSport,
  games,
  activeLeaguesData,
  activeLeagueFilter,
  onSelectLeagueFilter,
  activeLeagueGroup,
  onSelectLeagueGroup,
  onDynamicLeagueClick,
  teamSearchQuery,
  onSearchChange,
  searchResults,
  onSelectSearchResult,
  onShowAllResults,
  onClearSearch,
  searchedGame,
  searchedGamesCount = 0,
  onClearSearchedGame,
  betSlip,
  favoriteTeams,
  onAddToBetSlip,
  onLoadProps,
  onToggleFavorite,
  getFilteredGames,
}) {
  const [expanded, setExpanded] = useState(false);
  const [prevActiveSport, setPrevActiveSport] = useState(activeSport);
  const mainTabScroll = useDragScroll();
  const subTabScroll = useDragScroll();
  const leagueGroupScroll = useDragScroll();
  const leagueFilterScroll = useDragScroll();

  // Reset `expanded` synchronously during render (not via useEffect) when activeSport
  // changes, so the corrected value is used for this render's switcherVisible/preview
  // calculation instead of committing one stale-true frame before an effect fires.
  // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (activeSport !== prevActiveSport) {
    setPrevActiveSport(activeSport);
    setExpanded(false);
  }

  const sportGames = activeSport && activeSport !== 'all' ? (games[activeSport] || []) : [];
  const leagueSet = new Set(sportGames.map(g => g.league || '').filter(Boolean));
  const leagueList = Array.from(leagueSet).sort();

  // Sports with many same-title leagues (esports games, MMA/boxing promotions,
  // tennis tiers, ...) group by the "<Group> - <Rest>" prefix odds-api already
  // names them with, instead of one long flat row — see homeTabHelpers.
  const leagueGroups = groupLeaguesByPrefix(leagueList);
  const hasLeagueGroups = shouldGroupLeagues(leagueGroups, leagueList.length);
  const groupNames = Array.from(leagueGroups.keys()).sort();
  const activeGroup = activeLeagueGroup || groupNames[0];
  const leaguesInActiveGroup = hasLeagueGroups ? (leagueGroups.get(activeGroup) || []) : leagueList;
  const activeFilter = activeLeagueFilter || (hasLeagueGroups ? null : leagueList[0]);

  const categoryOfActiveSport = getCategoryOfActiveSport(activeSport);
  const activeCategoryLeagueCount = categoryOfActiveSport ? getAllowedLeagueCount(categoryOfActiveSport, enabledSports) : 0;
  const activeCategoryPrimaryKey = categoryOfActiveSport ? getPrimaryLeagueKey(categoryOfActiveSport, enabledSports) : null;
  const isOnNonPrimaryLeague = categoryOfActiveSport && activeSport !== activeCategoryPrimaryKey;
  const switcherVisible = categoryOfActiveSport && activeCategoryLeagueCount > 1 && (expanded || isOnNonPrimaryLeague);

  const allGames = getFilteredGames();
  const { visible, hasMore, remainingCount } = getUpcomingPreview(allGames, PREVIEW_COUNT, expanded);

  return (
    <div style={{ fontFamily }}>
      {/* Subtle always-visible search — mirrors the 3 result types from CustomerView's
          searchTeams() ('league' | 'dynamic_league' | 'game'), each with its own fields. */}
      <div className="px-3 py-1">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: colors.textFaint }} />
          <input
            type="text"
            placeholder="Search leagues or teams…"
            value={teamSearchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter' && searchResults.length > 0) onShowAllResults(); }}
            className="w-full pl-9 pr-8 py-1.5 rounded-lg text-sm focus:outline-none"
            style={{ background: colors.sectionBg, color: colors.textPrimary }}
          />
          {teamSearchQuery && (
            <button onClick={onClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4" style={{ color: colors.textFaint }} />
            </button>
          )}
        </div>

        {searchResults.length > 0 && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-lg" style={{ background: colors.sectionBg }}>
            <div className="p-2">
              <p className="text-[11px] mb-1.5" style={{ color: colors.textMuted }}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} — click to jump
              </p>
              {searchResults.map((result, idx) => {
                if (result.type === 'league' || result.type === 'dynamic_league') {
                  return (
                    <button
                      key={idx}
                      onClick={() => onSelectSearchResult(result)}
                      className="w-full text-left px-3 py-2 rounded-lg text-xs mb-1 flex items-center gap-2"
                      style={{ color: colors.textPrimary, background: colors.cardBg }}
                    >
                      <span className="text-base">{result.icon}</span>
                      <div>
                        <span className="font-semibold">{result.leagueName}</span>
                        <span className="ml-2 text-[10px]" style={{ color: colors.textFaint }}>{result.category}</span>
                      </div>
                    </button>
                  );
                }
                const config = getSportConfig(result.sportKey);
                return (
                  <button
                    key={idx}
                    onClick={() => onSelectSearchResult(result)}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs mb-1"
                    style={{ color: colors.textPrimary, background: colors.cardBg }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span>{config.icon}</span>
                      <span className="font-medium">{config.name}</span>
                    </div>
                    <div className="truncate" style={{ color: colors.textMuted }}>
                      <span className="font-semibold">{result.matchedTeam}</span>
                      <span className="mx-1" style={{ color: colors.textFaint }}>in</span>
                      <span>{result.game.away_team} @ {result.game.home_team}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(searchedGame || searchedGamesCount > 0) && (
          <div className="mt-2 flex items-center justify-between rounded-lg p-2" style={{ background: colors.sectionBg }}>
            <span className="text-xs" style={{ color: colors.textPrimary }}>
              {searchedGame
                ? `Showing: ${searchedGame.away_team} @ ${searchedGame.home_team}`
                : `Showing ${searchedGamesCount} matching game${searchedGamesCount === 1 ? '' : 's'}`}
            </span>
            <button
              onClick={onClearSearchedGame}
              className="text-xs px-2 py-1 rounded-lg"
              style={{ background: colors.goldActiveBg, color: colors.gold }}
            >
              Show All
            </button>
          </div>
        )}
      </div>

      {/* Sport category bar */}
      <div
        ref={mainTabScroll.ref}
        className="flex items-center gap-1 px-3 py-1.5 overflow-x-auto scrollbar-hide drag-scroll"
        onMouseDown={mainTabScroll.onMouseDown}
        onMouseLeave={mainTabScroll.onMouseLeave}
        onMouseUp={mainTabScroll.onMouseUp}
        onMouseMove={mainTabScroll.onMouseMove}
      >
        {Object.entries(SPORT_CATEGORIES).map(([category, sportKeys]) => {
          const allowedKeys = enabledSports ? sportKeys.filter(k => enabledSports.includes(k)) : sportKeys;
          if (allowedKeys.length === 0) return null;
          const primaryKey = allowedKeys[0];
          const isThisCategoryActive = categoryOfActiveSport === category;
          const config = categoryConfig[category] || { icon: '🎯', abbrev: category };
          const label = (isThisCategoryActive && switcherVisible) ? config.abbrev : (getSportConfig(primaryKey).name || config.abbrev);

          return (
            <button
              key={category}
              onClick={() => onSelectCategory(category)}
              className="flex-shrink-0 flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded-lg transition min-w-[56px] h-10 md:h-14"
              style={{ background: isThisCategoryActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
            >
              <span className="text-base md:text-xl leading-none">{config.icon}</span>
              <span className="text-[9px] md:text-[10px] font-medium whitespace-nowrap leading-none">{label}</span>
            </button>
          );
        })}
      </div>

      {/* League switcher row — only for the active category, only once "See all" has
          been pressed or the user is on a non-primary league within it */}
      {switcherVisible && (
        <div className="flex items-center gap-2 px-3">
        <div
          ref={subTabScroll.ref}
          className="flex items-center gap-1.5 py-1.5 overflow-x-auto scrollbar-hide drag-scroll flex-1 min-w-0"
          onMouseDown={subTabScroll.onMouseDown}
          onMouseLeave={subTabScroll.onMouseLeave}
          onMouseUp={subTabScroll.onMouseUp}
          onMouseMove={subTabScroll.onMouseMove}
        >
          {(() => {
            const sportKeys = SPORT_CATEGORIES[categoryOfActiveSport] || [];
            const allowedSportKeys = enabledSports ? sportKeys.filter(k => enabledSports.includes(k)) : sportKeys;
            const permanentTabs = allowedSportKeys.filter(key => PERMANENT_SPORT_KEYS.has(key));
            const cats = CATEGORY_TO_CATS[categoryOfActiveSport] || [];
            const isLoadingDynamic = cats.some(c => activeLeaguesData[c]?.loading);
            const dynamicLeagues = cats.flatMap(c => {
              const leagues = activeLeaguesData[c]?.leagues || [];
              return leagues.filter(l => !l.sport_key || !PERMANENT_SPORT_KEYS.has(l.sport_key)).map(l => ({ ...l, cat: c }));
            });

            return (
              <>
                {permanentTabs.map(sportKey => {
                  const config = getSportConfig(sportKey);
                  const isActive = activeSport === sportKey;
                  return (
                    <button
                      key={sportKey}
                      onClick={() => onSelectSport(sportKey)}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition text-xs md:text-sm"
                      style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
                    >
                      <span className="text-base">{config.icon}</span>
                      <span className="font-medium whitespace-nowrap">{config.name}</span>
                    </button>
                  );
                })}
                {isLoadingDynamic && (
                  <div className="flex items-center gap-1.5 px-3 py-2 text-xs" style={{ color: colors.textFaint }}>
                    <RefreshCw className="w-3 h-3 animate-spin" /> Loading…
                  </div>
                )}
                {dynamicLeagues.map(league => {
                  const dynKey = `dynamic::${league.cat}::${league.id}`;
                  const isActive = activeSport === dynKey;
                  const cleanName = league.name.includes(':') ? league.name.split(':').slice(1).join(':').trim() : league.name;
                  return (
                    <button
                      key={dynKey}
                      onClick={() => onDynamicLeagueClick(league.cat, league.id, league.name)}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition text-xs md:text-sm"
                      style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
                    >
                      <span className="font-medium whitespace-nowrap">{cleanName}</span>
                    </button>
                  );
                })}
              </>
            );
          })()}
        </div>
        {expanded && allGames.length > PREVIEW_COUNT && (
          <button
            onClick={() => setExpanded(false)}
            className="flex-shrink-0 text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg"
            style={{ background: colors.sectionBg, color: colors.textPrimary }}
          >
            Show less
          </button>
        )}
        </div>
      )}

      {/* League group row — only for sports whose leagues group meaningfully
          (esports by game, MMA/boxing by promotion, tennis by tour tier, ...) */}
      {activeSport && activeSport !== 'all' && hasLeagueGroups && (
        <div
          ref={leagueGroupScroll.ref}
          className="flex items-center gap-1.5 px-3 py-1.5 overflow-x-auto scrollbar-hide drag-scroll"
          onMouseDown={leagueGroupScroll.onMouseDown}
          onMouseLeave={leagueGroupScroll.onMouseLeave}
          onMouseUp={leagueGroupScroll.onMouseUp}
          onMouseMove={leagueGroupScroll.onMouseMove}
        >
          {groupNames.map(group => {
            const isActive = activeGroup === group;
            return (
              <button
                key={group}
                onClick={() => onSelectLeagueGroup(group)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-lg transition text-xs font-semibold"
                style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
              >
                <span>{group}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* League filter row — the flat list for ungrouped sports, or the
          expanded set of leagues within the selected group */}
      {activeSport && activeSport !== 'all' && leaguesInActiveGroup.length > 1 && (
        <div
          ref={leagueFilterScroll.ref}
          className="flex items-center gap-1.5 px-3 py-1.5 overflow-x-auto scrollbar-hide drag-scroll"
          onMouseDown={leagueFilterScroll.onMouseDown}
          onMouseLeave={leagueFilterScroll.onMouseLeave}
          onMouseUp={leagueFilterScroll.onMouseUp}
          onMouseMove={leagueFilterScroll.onMouseMove}
        >
          {leaguesInActiveGroup.map(league => {
            const isActive = activeFilter === league;
            const label = hasLeagueGroups ? splitLeagueName(league).sub : league;
            return (
              <button
                key={league}
                onClick={() => onSelectLeagueFilter(league)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-lg transition text-xs"
                style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
              >
                <span className="font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Game list */}
      <div className="px-3 pb-24 flex flex-col gap-1 mt-0.5">
        {visible.length === 0 ? (
          <div className="text-center py-12 text-sm" style={{ color: colors.textFaint }}>No games available right now</div>
        ) : (
          visible.map(game => (
            <GameCard
              key={game.id}
              game={game}
              onAddToBetSlip={onAddToBetSlip}
              onLoadProps={onLoadProps}
              betSlip={betSlip}
              favoriteTeams={favoriteTeams}
              onToggleFavorite={onToggleFavorite}
            />
          ))
        )}
        {hasMore && (
          <button
            onClick={() => setExpanded(true)}
            className="w-full py-2.5 rounded-lg text-sm font-medium"
            style={{ background: colors.sectionBg, color: colors.textPrimary }}
          >
            See all {remainingCount + PREVIEW_COUNT} games
          </button>
        )}
        {expanded && allGames.length > PREVIEW_COUNT && (
          <button
            onClick={() => setExpanded(false)}
            className="w-full py-2.5 rounded-lg text-sm font-medium"
            style={{ background: colors.sectionBg, color: colors.textPrimary }}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}
